/**
 * JavaScript 瀑布流布局组件
 * 使用真正的瀑布流算法：将卡片分配到最短的列
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'

interface MasonryGridProps {
  children: React.ReactNode[]
  gap?: number // 列间距，默认 20px
  columnWidth?: number // 每列最小宽度，用于计算列数
  // 响应式列数配置（可选，如果不提供则根据 columnWidth 自动计算）
  responsiveColumns?: {
    desktop: number // > 1600px
    laptop: number // 1200px - 1600px
    tablet: number // 900px - 1200px
    mobile: number // < 900px
  }
  // 是否基于屏幕宽度而非容器宽度计算列数（对于居中布局的页面建议开启）
  useScreenWidth?: boolean
}

// 解析 aspectRatio 字符串获取高度估算比例
const parseAspectRatio = (aspectRatio: string): number => {
  // aspectRatio 格式如 '3/4', '4/3', '1/1'
  const parts = aspectRatio.split('/')
  if (parts.length === 2) {
    const width = parseFloat(parts[0]) || 1
    const height = parseFloat(parts[1]) || 1
    return height / width // 返回高度相对于宽度的比例
  }
  return 1 // 默认比例（如 '3/4' 即高度是宽度的 4/3）
}

// 估算单个卡片的高度
const estimateCardHeight = (child: React.ReactNode, columnWidth: number): number => {
  // 尝试从 child props 中获取 aspectRatio
  const childProps = (child as React.ReactElement<any>)?.props
  const aspectRatio = childProps?.aspectRatio || '3/4'
  const ratio = parseAspectRatio(aspectRatio)

  // 基础高度 = 图片高度 + 内容区域高度
  // 内容区域大约 60-80px（标题 + 作者信息 + 点赞）
  const imageHeight = columnWidth * ratio
  const contentHeight = 70 // 估算的内容区域高度

  return imageHeight + contentHeight
}

const MasonryGrid: React.FC<MasonryGridProps> = ({
  children,
  gap = 20,
  columnWidth = 280,
  responsiveColumns,
  useScreenWidth = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  // 计算当前列数
  const getColumnCount = useCallback((width: number): number => {
    if (responsiveColumns) {
      // 使用用户配置的响应式列数
      if (width > 1600) return responsiveColumns.desktop
      if (width > 1200) return responsiveColumns.laptop
      if (width > 900) return responsiveColumns.tablet
      return responsiveColumns.mobile
    }
    // 自动计算列数
    return Math.max(2, Math.floor((width + gap) / (columnWidth + gap)))
  }, [responsiveColumns, columnWidth, gap])

  // 根据配置决定使用屏幕宽度还是容器宽度
  const effectiveWidth = useScreenWidth ? screenWidth : containerWidth
  const columnCount = getColumnCount(effectiveWidth)

  // 计算每列的实际宽度（基于容器宽度）
  const actualColumnWidth = containerWidth > 0
    ? (containerWidth - gap * (columnCount - 1)) / columnCount
    : columnWidth

  // 监听容器宽度变化
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [])

  // 监听屏幕宽度变化（如果使用屏幕宽度计算列数）
  useEffect(() => {
    if (!useScreenWidth) return

    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [useScreenWidth])

  // 将卡片分配到列中（真正的瀑布流算法）
  const distributeToColumns = useCallback((
    items: React.ReactNode[],
    colCount: number,
    colWidth: number,
  ): React.ReactNode[][] => {
    const columns: React.ReactNode[][] = Array.from({ length: colCount }, () => [])
    const heights = Array(colCount).fill(0)

    items.forEach((item) => {
      // 找到最短的列
      const minHeightIndex = heights.indexOf(Math.min(...heights))
      // 估算卡片高度
      const estimatedHeight = estimateCardHeight(item, colWidth)

      columns[minHeightIndex].push(item)
      heights[minHeightIndex] += estimatedHeight + gap / 2 // 卡片间距
    })

    return columns
  }, [gap])

  // 分配卡片到各列
  const columnData = useMemo(() => {
    return distributeToColumns(
      children,
      columnCount,
      actualColumnWidth,
    )
  }, [children, columnCount, actualColumnWidth, distributeToColumns])

  return (
    <div
      ref={containerRef}
      className="masonry-grid"
      style={{
        display: 'flex',
        gap: `${gap}px`,
      }}
    >
      {columnData.map((columnItems, columnIndex) => (
        <div
          key={columnIndex}
          className="masonry-column"
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: `${gap}px`,
            minWidth: 0, // 防止内容溢出
          }}
        >
          {columnItems.map((item, itemIndex) => {
            // 从 item 中提取 key（如果有）
            const itemKey = (item as React.ReactElement<any>)?.key || `${columnIndex}-${itemIndex}`
            return (
              <div
                key={itemKey}
                className="masonry-item"
              >
                {item}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default MasonryGrid